import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { NAV } from "@/data/navigation";
import { COMPANY } from "@/data/company";
import { Logo } from "./Logo";
import { Btn } from "./Btn";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [location.pathname]);

  return (
    <>
      {/* Top info bar */}
      <div className="hidden lg:block bg-ink-900 border-b border-white/5 text-xs">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between py-2 text-chrome-mid font-mono uppercase tracking-wider">
          <span>{COMPANY.baseline} — France &amp; Suisse</span>
          <div className="flex items-center gap-6">
            <a href={COMPANY.emailHref} className="hover:text-white transition-colors">{COMPANY.email}</a>
            <a href={COMPANY.phoneHref} className="flex items-center gap-2 hover:text-white transition-colors">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signature opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-signature" />
              </span>
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      <header
        className={`fixed top-0 lg:top-[33px] z-50 w-full transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-ink-900/90 border-b border-white/10 shadow-xl shadow-black/40" : "bg-ink-900/60 backdrop-blur-md border-b border-transparent"
        }`}
        onMouseLeave={() => setOpenMega(null)}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 lg:h-[72px] items-center justify-between gap-4">
            <Logo className="h-8 lg:h-10" />

            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {NAV.map((item) =>
                item.mega ? (
                  <div key={item.label} onMouseEnter={() => setOpenMega(item.label)} className="relative">
                    <button
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        openMega === item.label ? "text-white bg-white/5" : "text-chrome-light hover:text-white"
                      }`}
                      data-testid={`nav-${item.label}`}
                    >
                      {item.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openMega === item.label ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="px-3 py-2 text-sm font-medium text-chrome-light hover:text-white rounded-md transition-colors"
                    data-testid={`nav-${item.label}`}
                  >
                    {item.label}
                  </Link>
                ),
              )}
            </nav>

            <div className="flex items-center gap-2">
              <Btn to="/contact/" size="sm" className="hidden sm:inline-flex" data-testid="header-devis-btn">
                Devis
              </Btn>
              <button
                className="lg:hidden p-2 text-white"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {openMega && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="hidden lg:block absolute left-0 right-0 top-full border-b border-white/10 bg-ink-800/98 backdrop-blur-xl shadow-2xl"
              data-testid="mega-menu"
            >
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-3 gap-8">
                  {NAV.find((n) => n.label === openMega)?.columns.map((col) => (
                    <div key={col.heading}>
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-signature mb-4">{col.heading}</p>
                      <ul className="space-y-1">
                        {col.links.map((l) => (
                          <li key={l.to}>
                            <Link
                              to={l.to}
                              className="group flex items-center justify-between rounded-md px-3 py-2 text-sm text-chrome-light hover:text-white hover:bg-white/5 transition-colors"
                            >
                              {l.label}
                              <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="rounded-xl border border-white/10 bg-ink-700 p-6 flex flex-col justify-between">
                    <div>
                      <p className="text-white font-semibold text-lg">Besoin d'un conseil ?</p>
                      <p className="text-sm text-slate-400 mt-2">Étude et devis gratuits, de la conception à la pose.</p>
                    </div>
                    <Btn to="/contact/" size="sm" className="mt-4 w-full">Demander un devis</Btn>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-40 bg-ink-900/98 backdrop-blur-xl pt-20 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-5 pb-10">
              {NAV.map((item) =>
                item.mega ? (
                  <div key={item.label} className="border-b border-white/10">
                    <button
                      className="flex w-full items-center justify-between py-4 text-left text-lg font-medium text-white"
                      onClick={() => setMobileSub(mobileSub === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown className={`h-5 w-5 transition-transform ${mobileSub === item.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileSub === item.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-4 pl-3 space-y-1">
                            {item.columns.flatMap((c) => c.links).map((l) => (
                              <Link key={l.to} to={l.to} className="block py-2.5 text-chrome-light hover:text-signature">
                                {l.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link key={item.label} to={item.to} className="block border-b border-white/10 py-4 text-lg font-medium text-white">
                    {item.label}
                  </Link>
                ),
              )}
              <div className="mt-6 flex flex-col gap-3">
                <Btn to="/contact/" size="lg" className="w-full">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
                <Btn href={COMPANY.phoneHref} variant="secondary" size="lg" className="w-full">
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </Btn>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
